/**
 * Suggested issues queue (P4) — standup-mined candidates awaiting human push to Jira.
 * Distinct from Plan-tab Proposed changes.
 */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { EmptyState, Icon, SectionCard } from "../../mui";
import {
  listProjectActions,
  pushSuggestedIssue,
  updateProjectAction,
  type ProjectAction,
} from "../../lib/brain-api";

const c = brainTokens.color;

export function SuggestedIssuesPanel({
  projectId,
  embedded = false,
  onChanged,
  onCount,
}: {
  projectId: string;
  /** Skip SectionCard chrome when nested under AdminStrips. */
  embedded?: boolean;
  onChanged?: () => void;
  onCount?: (n: number) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ProjectAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState<ProjectAction | null>(null);
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const all = await listProjectActions({ project: projectId, status: "suggested" });
      setItems(all);
      onCount?.(all.length);
    } catch {
      setItems([]);
      onCount?.(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [projectId]);

  const dismiss = async (id: string) => {
    try {
      await updateProjectAction(id, { status: "dismissed" });
      await load();
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Dismiss failed", { variant: "error" });
    }
  };

  const push = async () => {
    if (!accepting || !summary.trim()) return;
    setBusy(true);
    try {
      const created = await pushSuggestedIssue({
        actionId: accepting.id,
        projectId,
        summary: summary.trim(),
        description: description.trim() || summary.trim(),
      });
      enqueueSnackbar(`Created ${created.key}`, { variant: "success" });
      setAccepting(null);
      await load();
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Push failed", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <>
      {loading ? (
        <CircularProgress size={18} sx={{ color: c.teal }} />
      ) : items.length === 0 ? (
        embedded ? null : (
          <EmptyState icon="inbox" title="No suggested issues">
            Standup synthesis will queue candidates here — nothing auto-creates in Jira.
          </EmptyState>
        )
      ) : (
        items.map((a) => (
          <Box
            key={a.id}
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              py: 1.25,
              borderTop: `1px solid ${c.borderLight}`,
              "&:first-of-type": { borderTop: "none", pt: 0 },
            }}
          >
            <Box sx={{ flex: 1, fontSize: 13.5, color: c.ink2, lineHeight: 1.5 }}>{a.text}</Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setAccepting(a);
                setSummary(a.text);
                setDescription("");
              }}
            >
              Accept
            </Button>
            <Button size="small" onClick={() => void dismiss(a.id)}>
              Dismiss
            </Button>
          </Box>
        ))
      )}

      <Dialog open={Boolean(accepting)} onClose={() => setAccepting(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Jira issue</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Box sx={{ fontSize: 13, color: c.muted2 }}>
            Review the AI best-guess, then push. Nothing is created until you confirm.
          </Box>
          <TextField
            label="Summary"
            size="small"
            fullWidth
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccepting(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !summary.trim()}
            onClick={() => void push()}
            startIcon={<Icon name="external-link" size={14} />}
          >
            Push to Jira
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (embedded) return <Box>{body}</Box>;

  return <SectionCard label={`Suggested issues · ${items.length}`}>{body}</SectionCard>;
}
